<template>
  <div
    class="recipient-picker"
    :class="{ 'recipient-picker--disabled': disabled, 'recipient-picker--error': !!error }"
  >
    <div class="recipient-picker__tabs" role="tablist" aria-label="Fuentes de destinatarios">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        role="tab"
        class="recipient-picker__tab terrain-focusable"
        :class="{ 'recipient-picker__tab--active': activeTab === tab.key }"
        :aria-selected="activeTab === tab.key"
        :disabled="disabled"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
        <span v-if="tab.count" class="recipient-picker__tab-count">{{ tab.count }}</span>
      </button>
    </div>

    <div class="recipient-picker__panel">
      <div v-show="activeTab === 'emails'" class="recipient-picker__email-entry">
        <TagMultiSelect
          :value="pendingEmails"
          :options="userOptions"
          :loading="usersLoading"
          :disabled="disabled"
          :show-count="false"
          :allow-custom="true"
          :custom-validator="isValidEmail"
          :custom-normalizer="normalizedEmail"
          placeholder="Buscar, agregar o pegar correos separados por ;…"
          empty-message="Sin usuarios"
          @input="updateEmails"
          @search="searchUsers"
        />
        <p v-if="pendingEmails.length && pendingEmails.length !== selection.emails.length" class="recipient-picker__preview-help">
          Pendiente de guardar — el resto ya está en la lista de destinatarios.
        </p>
      </div>
      <TagMultiSelect
        v-show="activeTab === 'groups'"
        :value="selection.groupIds"
        :options="groupOptions"
        :loading="groupsLoading"
        :disabled="disabled"
        :show-chips="false"
        :show-selected-options="true"
        :show-count="false"
        placeholder="Buscar grupos…"
        empty-message="Sin grupos"
        @input="updateGroupIds"
        @search="searchGroups"
      />
      <TagMultiSelect
        v-show="activeTab === 'crews'"
        :value="selection.crewIds"
        :options="crewOptions"
        :loading="crewsLoading"
        :disabled="disabled"
        :show-chips="false"
        :show-selected-options="true"
        :show-count="false"
        placeholder="Buscar cuadrillas…"
        empty-message="Sin cuadrillas"
        @input="updateCrewIds"
        @search="searchCrews"
      />
    </div>

    <div v-if="showPreview" class="recipient-picker__preview terrain-panel" aria-live="polite">
      <div class="recipient-picker__preview-header">
        <div>
          <p class="recipient-picker__preview-count">
            <strong>{{ previewLoading ? '…' : preview.total }}</strong>
            destinatario{{ preview.total === 1 ? '' : 's' }} efectivo{{ preview.total === 1 ? '' : 's' }}
          </p>
          <p class="recipient-picker__preview-help">Cada persona aparece una sola vez, aunque pertenezca a varias selecciones.</p>
        </div>
        <button
          v-if="excludedCount"
          type="button"
          class="recipient-picker__restore terrain-focusable"
          :disabled="disabled"
          @click="restoreExcludedRecipients"
        >
          Restaurar {{ excludedCount }} quitado{{ excludedCount === 1 ? '' : 's' }}
        </button>
      </div>

      <label v-if="preview.items.length || recipientSearch" class="recipient-picker__search">
        <span class="terrain-sr-only">Buscar en destinatarios efectivos</span>
        <input
          v-model.trim="recipientSearch"
          type="search"
          class="terrain-focusable"
          placeholder="Buscar destinatario o grupo…"
          @input="resetVisibleLimit"
        >
      </label>

      <div
        v-if="recipientSections.length"
        class="recipient-picker__preview-list"
        tabindex="0"
        aria-label="Destinatarios efectivos agrupados"
      >
        <section v-for="section in recipientSections" :key="section.key" class="recipient-picker__section">
          <h4 class="recipient-picker__section-title">
            <span>{{ section.label }}</span>
            <span>{{ section.total }}</span>
          </h4>
          <ul class="recipient-picker__section-list">
            <li v-for="item in section.items" :key="item.normalizedEmail" class="recipient-picker__preview-item">
              <div class="recipient-picker__recipient">
                <span class="recipient-picker__preview-username">{{ item.email }}</span>
                <span class="recipient-picker__preview-sources">
                  <span
                    v-for="source in item.sources"
					:key="`${source.type}-${source.sourceId || source.sourceKey}`"
                    class="recipient-picker__source-badge"
                  >{{ sourceLabel(source) }}</span>
                </span>
              </div>
              <button
                type="button"
                class="recipient-picker__remove terrain-focusable"
                :disabled="disabled"
                :aria-label="`Quitar a ${item.email} de los destinatarios`"
                title="Quitar destinatario"
                @click="removeRecipient(item)"
              >×</button>
            </li>
          </ul>
        </section>
      </div>
      <button
        v-if="hasMoreRecipients"
        type="button"
        class="recipient-picker__show-more terrain-focusable"
        @click="showMoreRecipients"
      >
        Mostrar {{ Math.min(RECIPIENT_PAGE_SIZE, filteredRecipients.length - visibleLimit) }} más
      </button>
      <p v-else-if="recipientSearch && !filteredRecipients.length" class="recipient-picker__preview-empty">
        No hay destinatarios que coincidan con la búsqueda.
      </p>
      <p v-else-if="!preview.items.length" class="recipient-picker__preview-empty">
        Selecciona usuarios, grupos o cuadrillas para ver la lista efectiva.
      </p>
    </div>

    <p v-if="error" class="terrain-error-text" role="alert">{{ error }}</p>
  </div>
</template>

<script>
// RecipientPicker.vue — selector compuesto de destinatarios de Campaña
// (Usuarios/Grupos/Cuadrillas en tabs) con preview en vivo de la lista
// efectiva vía GET /recipient-sources.
//
// Usuarios/Grupos se resuelven contra los endpoints ya existentes de la app
// (api/v1/users, api/v1/groups — mismo camino que restUser.js/restGroup.js,
// sin depender del mixin para mantener el componente autocontenido);
// Cuadrillas vía `api.listCrews` (services/terrain/api.js). `api` se recibe
// como prop, mismo patrón que AutosaveController.vue.
import TagMultiSelect from './TagMultiSelect.vue';

const SOURCE_LABELS = { email: 'Correo', individual: 'Directo', group: 'Grupo', crew: 'Cuadrilla' };
const SOURCE_ORDER = { shared: 0, individual: 1, group: 2, crew: 3 };
const RECIPIENT_PAGE_SIZE = 100;

function uniqueIds(ids) {
  return [...new Set((ids || []).filter(Boolean))];
}

const normalizedEmail = (value) => String(value || '').trim().toLowerCase();
// uniqueEmails guarda la clave ya normalizada (minúsculas), no el valor tal
// cual se escribió: userOptions (searchUsers) siempre trae sus ids en
// minúsculas, y TagMultiSelect compara `value` contra esos ids con === — un
// correo guardado con mayúsculas (historial previo a TagMultiSelect, o
// integrantes agregados vía "Agregar por grupo") nunca calzaba, así que
// aparecía seleccionable de nuevo en el listado y un click no lo sacaba
// (isSelected también compara por ===).
function uniqueEmails(values) {
  const seen = new Set();
  return (values || []).reduce((result, value) => {
    const key = normalizedEmail(value);
    if (!key || seen.has(key)) return result;
    seen.add(key);
    result.push(key);
    return result;
  }, []);
}

function normalizeSelection(value = {}) {
  const normalized = {
	emails: uniqueEmails(value.emails),
	excludedEmails: uniqueEmails(value.excludedEmails).map(normalizedEmail),
    userIds: uniqueIds(value.userIds),
    groupIds: uniqueIds(value.groupIds),
    crewIds: uniqueIds(value.crewIds),
    excludedUserIds: uniqueIds(value.excludedUserIds),
  };
  if (!normalized.emails.length && !normalized.userIds.length && !normalized.groupIds.length && !normalized.crewIds.length) {
    normalized.excludedUserIds = [];
	  normalized.excludedEmails = [];
  }
  return normalized;
}

export default {
  name: 'RecipientPicker',
  components: { TagMultiSelect },
  props: {
    value: {
      type: Object,
      default: () => ({ userIds: [], groupIds: [], crewIds: [], excludedUserIds: [] }),
    },
    api: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    error: { type: String, default: '' },
    // false cuando el consumidor ya muestra la lista efectiva por su cuenta
    // (ej. TerrainCampaignEdit "Destinatarios de la campaña publicada"), para
    // no repetir la misma lista de personas dos/tres veces en la pantalla.
    showPreview: { type: Boolean, default: true },
    // Correos ya activos/publicados que el consumidor muestra en su propia
    // lista (mismo caso que showPreview=false) — sirve para distinguir, en
    // los chips de "Correos", los que ya están ahí (no repetir) de los recién
    // agregados y aún sin guardar (sí mostrar: si no, agregar un correo no
    // da ninguna señal visible hasta hacer Guardar).
    activeEmails: { type: Array, default: () => [] },
  },
  data() {
    return {
      activeTab: 'emails',
      userOptions: [],
      groupOptions: [],
      crewOptions: [],
      usersLoading: false,
      groupsLoading: false,
      crewsLoading: false,
      preview: { items: [], total: 0 },
      previewLoading: false,
      previewSeq: 0,
      previewTimer: null,
      selection: normalizeSelection(this.value),
      recipientSearch: '',
      visibleLimit: RECIPIENT_PAGE_SIZE,
      RECIPIENT_PAGE_SIZE,
    };
  },
  computed: {
    tabs() {
      return [
		{ key: 'emails', label: 'Correos', count: this.selection.emails.length },
        { key: 'groups', label: 'Grupos', count: this.selection.groupIds.length },
        { key: 'crews', label: 'Cuadrillas', count: this.selection.crewIds.length },
      ];
    },
    excludedCount() {
	  return this.selection.excludedEmails.length + this.selection.excludedUserIds.length;
    },
    // Ver comentario de la prop activeEmails: solo los correos que todavía no
    // están en la lista activa del consumidor se muestran como chip — recién
    // agregados y pendientes de Guardar.
    pendingEmails() {
      const active = new Set((this.activeEmails || []).map(normalizedEmail));
      return this.selection.emails.filter((email) => !active.has(normalizedEmail(email)));
    },
    filteredRecipients() {
      const query = this.recipientSearch.toLowerCase();
      if (!query) return this.preview.items || [];
      return (this.preview.items || []).filter((item) => {
        const searchableSources = (item.sources || [])
          .map((source) => `${SOURCE_LABELS[source.type] || source.type} ${source.sourceNameSnapshot || ''}`)
          .join(' ');
		return `${item.email || ''} ${item.username || ''} ${searchableSources}`.toLowerCase().includes(query);
      });
    },
    recipientSections() {
	  const sections = [{ key: 'effective', label: 'Lista efectiva por correo', order: 0, items: this.filteredRecipients, total: this.filteredRecipients.length }];
      let remaining = this.visibleLimit;
      return sections.reduce((visibleSections, section) => {
        if (remaining <= 0) return visibleSections;
        const items = section.items.slice(0, remaining);
        remaining -= items.length;
        if (items.length) visibleSections.push({ ...section, items });
        return visibleSections;
      }, []);
    },
    hasMoreRecipients() {
      return this.filteredRecipients.length > this.visibleLimit;
    },
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        this.selection = normalizeSelection(value);
      },
    },
  },
  created() {
    this.searchUsers('');
    this.searchGroups('');
    this.searchCrews('');
	const { emails, userIds, groupIds, crewIds } = this.selection;
	if ((emails || []).length || (userIds || []).length || (groupIds || []).length || (crewIds || []).length) {
      this.refreshPreview(this.selection);
    }
  },
  beforeDestroy() {
    clearTimeout(this.previewTimer);
  },
  methods: {
    searchUsers(query) {
      this.usersLoading = true;
      const backendurl = this.$store.getters.backendurl;
      this.$http.get(`${backendurl}api/v1/users`, { params: { page: 1, size: 25, search: query || '' } })
        .then((response) => {
          this.userOptions = (response.data || []).map((user) => ({
			id: String(user.username || '').trim().toLowerCase(),
            label: `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.username,
            sublabel: user.username,
          }));
        })
        .catch(() => { this.userOptions = []; })
        .finally(() => { this.usersLoading = false; });
    },
    searchGroups(query) {
      this.groupsLoading = true;
      const backendurl = this.$store.getters.backendurl;
      this.$http.get(`${backendurl}api/v1/groups/`)
        .then((response) => {
          const all = (response.data || []).map((group) => ({ id: group.id, label: group.name }));
          const normalized = (query || '').trim().toLowerCase();
          this.groupOptions = normalized
            ? all.filter((group) => group.label.toLowerCase().includes(normalized))
            : all;
        })
        .catch(() => { this.groupOptions = []; })
        .finally(() => { this.groupsLoading = false; });
    },
    searchCrews(query) {
      this.crewsLoading = true;
      this.api.listCrews({ page: 1, size: 25 })
        .then((response) => {
          const items = (response.data && response.data.items) || [];
          const mapped = items.map((crew) => ({ id: crew.id, label: crew.name }));
          const normalized = (query || '').trim().toLowerCase();
          this.crewOptions = normalized
            ? mapped.filter((crew) => crew.label.toLowerCase().includes(normalized))
            : mapped;
        })
        .catch(() => { this.crewOptions = []; })
        .finally(() => { this.crewsLoading = false; });
    },
    updateUserIds(ids) {
      const selected = new Set(ids);
      this.emitValue({
        ...this.selection,
        userIds: ids,
        excludedUserIds: this.selection.excludedUserIds.filter((id) => !selected.has(id)),
      });
    },
    normalizedEmail,
    isValidEmail(email) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email); },
    updateEmails(emails) {
      const active = new Set((this.activeEmails || []).map(normalizedEmail));
      const alreadyActive = this.selection.emails.filter((email) => active.has(normalizedEmail(email)));
      const nextEmails = uniqueEmails([...alreadyActive, ...emails]);
      const selected = new Set(nextEmails.map(normalizedEmail));
      this.emitValue({
        ...this.selection,
        emails: nextEmails,
        excludedEmails: this.selection.excludedEmails.filter((email) => !selected.has(normalizedEmail(email))),
      });
    },
    updateGroupIds(ids) { this.emitValue({ ...this.selection, groupIds: ids }); },
    updateCrewIds(ids) { this.emitValue({ ...this.selection, crewIds: ids }); },
    emitValue(next) {
      const normalized = normalizeSelection(next);
      this.selection = normalized;
      this.$emit('input', normalized);
      this.refreshPreview(normalized);
    },
    removeRecipient(item) {
	  const hasCollectiveSource = (item.sources || []).some((source) => source.type !== 'individual' && source.type !== 'email');
	  const email = item.normalizedEmail;
      const next = {
        ...this.selection,
		emails: this.selection.emails.filter((value) => normalizedEmail(value) !== email),
      };
      if (hasCollectiveSource) {
		next.excludedEmails = uniqueIds([...this.selection.excludedEmails, email]);
      } else {
		next.excludedEmails = this.selection.excludedEmails.filter((value) => value !== email);
      }
      // Ambas ramas de arriba dejan a item.userId fuera de la lista efectiva
      // (exclusión explícita o remoción directa, sin otra fuente que lo siga
      // cubriendo): se quita de inmediato del preview ya cargado en vez de
      // esperar el refresco debounced de abajo (400ms + red), que si no
      // dejaba a la persona removida visible varios cientos de ms como si
      // el click no hubiera hecho nada.
      this.preview = {
        ...this.preview,
		items: this.preview.items.filter((existing) => existing.normalizedEmail !== email),
        total: Math.max(0, this.preview.total - 1),
      };
      this.emitValue(next);
    },
    restoreExcludedRecipients() {
	  this.emitValue({ ...this.selection, excludedUserIds: [], excludedEmails: [] });
    },
    resetVisibleLimit() {
      this.visibleLimit = RECIPIENT_PAGE_SIZE;
    },
    showMoreRecipients() {
      this.visibleLimit += RECIPIENT_PAGE_SIZE;
    },
    refreshPreview(selection) {
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => this.fetchPreview(selection), 400);
    },
    fetchPreview(selection) {
      const userIds = selection.userIds || [];
	  const emails = selection.emails || [];
      const groupIds = selection.groupIds || [];
      const crewIds = selection.crewIds || [];
      const excludedUserIds = selection.excludedUserIds || [];
	  const excludedEmails = selection.excludedEmails || [];
	  if (!emails.length && !userIds.length && !groupIds.length && !crewIds.length) {
        this.previewSeq += 1;
        this.previewLoading = false;
        this.preview = { items: [], total: 0 };
        return;
      }
      const seq = ++this.previewSeq;
      this.previewLoading = true;
      this.api.getRecipientSources({
		emails, userIds, groupIds, crewIds, excludedUserIds, excludedEmails,
      })
        .then((response) => {
          if (seq !== this.previewSeq) return;
          this.preview = response.data || { items: [], total: 0 };
        })
        .catch(() => {
          if (seq !== this.previewSeq) return;
          this.preview = { items: [], total: 0 };
        })
        .finally(() => {
          if (seq === this.previewSeq) this.previewLoading = false;
        });
    },
    sectionInfo(item) {
      const sources = item.sources || [];
      if (sources.length > 1) {
        return {
          key: 'shared',
          label: 'Personas en varias selecciones',
          order: SOURCE_ORDER.shared,
        };
      }
      const source = sources[0] || { type: 'individual', sourceId: 'unknown' };
      if (source.type === 'individual') {
        return {
          key: 'individual',
          label: 'Añadidos individualmente',
          order: SOURCE_ORDER.individual,
        };
      }
      return {
        key: `${source.type}-${source.sourceId}`,
        label: source.sourceNameSnapshot || SOURCE_LABELS[source.type] || source.type,
        order: SOURCE_ORDER[source.type] || 99,
      };
    },
    sourceLabel(source) {
      const type = SOURCE_LABELS[source.type] || source.type;
      if (source.type === 'individual') return type;
      return `${type}: ${source.sourceNameSnapshot || source.sourceId}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.recipient-picker {
  display: flex;
  flex-direction: column;
  gap: var(--terrain-space-md);
  width: 100%;

  &--disabled { opacity: 0.7; }
}

.recipient-picker__tabs {
  display: flex;
  gap: var(--terrain-space-xs);
  border-bottom: 1px solid var(--color-line);
}

.recipient-picker__tab {
  display: inline-flex;
  align-items: center;
  gap: var(--terrain-space-xs);
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--color-ghost);
  font-size: var(--terrain-font-body);
  font-weight: 500;
  cursor: pointer;
  transition: color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover:not(:disabled) { color: var(--color-navy); }
  &:disabled { cursor: not-allowed; }

  &--active {
    color: var(--color-navy);
    font-weight: 600;
    border-bottom-color: var(--color-orange);
  }
}

.recipient-picker__tab-count {
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: var(--color-mist);
  border-radius: 999px;
  color: var(--color-navy);
  font-size: 0.72rem;
  font-weight: 700;
}

.recipient-picker__panel {
  display: flex;
  flex-direction: column;
  gap: var(--terrain-space-sm);
}

.recipient-picker__email-entry {
  display: flex;
  flex-direction: column;
  gap: var(--terrain-space-sm);
}

.recipient-picker__preview {
  display: flex;
  flex-direction: column;
  gap: var(--terrain-space-sm);
  padding: var(--terrain-space-md);
}

.recipient-picker__preview-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--terrain-space-md);
}

.recipient-picker__preview-count {
  margin: 0;
  color: var(--color-graphite);
  font-size: var(--terrain-font-body);

  strong { color: var(--color-navy); }
}

.recipient-picker__preview-help {
  margin: 2px 0 0;
  color: var(--color-ghost);
  font-size: var(--terrain-font-caption);
}

.recipient-picker__restore,
.recipient-picker__show-more {
  flex: 0 0 auto;
  padding: var(--terrain-space-xs) var(--terrain-space-sm);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  background: var(--color-white);
  color: var(--color-steel);
  font-size: var(--terrain-font-caption);
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { border-color: var(--color-steel); }
  &:disabled { cursor: not-allowed; opacity: 0.6; }
}

.recipient-picker__search {
  display: block;

  input {
    width: 100%;
    padding: var(--terrain-space-sm) var(--terrain-space-md);
    border: 1px solid var(--color-line);
    border-radius: var(--terrain-radius-md);
    background: var(--color-white);
    color: var(--color-graphite);
    font-size: var(--terrain-font-body);

    &::placeholder { color: var(--color-ghost); }
  }
}

.recipient-picker__preview-list {
  display: flex;
  flex-direction: column;
  gap: var(--terrain-space-md);
  max-height: 360px;
  overflow-y: auto;
  padding-right: var(--terrain-space-xs);
}

.recipient-picker__section {
  display: flex;
  flex-direction: column;
  gap: var(--terrain-space-xs);
}

.recipient-picker__section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--terrain-space-sm);
  padding: 0 var(--terrain-space-sm);
  margin: 0;
  color: var(--color-navy);
  font-size: var(--terrain-font-caption);
  font-weight: 700;
}

.recipient-picker__section-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.recipient-picker__preview-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--terrain-space-sm);
  min-height: 40px;
  padding: var(--terrain-space-xs) var(--terrain-space-xs) var(--terrain-space-xs) var(--terrain-space-sm);
  border-radius: var(--terrain-radius-sm);

  &:hover { background: var(--color-mist); }
}

.recipient-picker__recipient {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--terrain-space-xs) var(--terrain-space-sm);
  min-width: 0;
}

.recipient-picker__preview-username {
  color: var(--color-graphite);
  font-size: var(--terrain-font-body);
  overflow-wrap: anywhere;
}

.recipient-picker__preview-sources {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-xs);
}

.recipient-picker__source-badge {
  padding: 1px var(--terrain-space-xs);
  border-radius: 999px;
  background: var(--color-mist);
  color: var(--color-ghost);
  font-size: var(--terrain-font-caption);
}

.recipient-picker__remove {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 50%;
  background: transparent;
  color: var(--color-ghost);
  font-size: 1.15rem;
  line-height: 1;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--color-line);
    background: var(--color-white);
    color: var(--color-danger);
  }
  &:disabled { cursor: not-allowed; opacity: 0.5; }
}

.recipient-picker__show-more {
  align-self: center;
}

.recipient-picker__preview-empty {
  margin: 0;
  color: var(--color-ghost);
  font-size: var(--terrain-font-body);
  font-style: italic;
}

@media (max-width: 640px) {
  .recipient-picker__preview-header { flex-direction: column; }

  .recipient-picker__restore { align-self: flex-start; }
}
</style>
